import { Fragment, useMemo } from 'react'
import { CharacterClassIcon } from 'app/frontend/base/components'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { CharacterClass } from 'app/frontend/types/custom'
import CharacterRole from 'app/frontend/types/custom/CharacterRole'
import { simpleCharacterRoles } from 'app/frontend/types/custom/SimpleCharacterRole'
import { RaidPlanTimeline } from 'app/frontend/types/custom/RaidPlan'
import Card from 'app/frontend/base/components/Card'
import {
  getAllSpecializations,
  getClass,
} from 'app/frontend/base/services/characterValues'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { getTeamPlanActorSortMeta } from './teamPlanActorSorting'
import { getEncounterAssignmentAbilities } from './assignmentTimeline'

type Props = {
  timeline: RaidPlanTimeline
  className?: string
}

type ActorSummary = {
  key: string
  className: string
  specName?: string
  specIcon?: string
}

type SimpleRole = typeof simpleCharacterRoles[number]

const TeamPlanCompositionSummary = ({ timeline, className }: Props) => {
  const specializationsById = useMemo(() => {
    return Object.entries(getAllSpecializations()).reduce<
      Record<
        number,
        {
          id: number
          name: string
          icon: string
          className: string
          defaultRole: CharacterRole
        }
      >
    >((acc, [id, specialization]) => {
      if (typeof specialization.class_id !== 'number') return acc
      acc[Number(id)] = {
        id: Number(id),
        name: specialization.name,
        icon: specialization.icon,
        className:
          getClass(specialization.class_id.toString()) || 'Unknown class',
        defaultRole: specialization.default_role as CharacterRole,
      }
      return acc
    }, {})
  }, [])

  const specializations = useMemo(() => {
    return Object.values(specializationsById)
  }, [specializationsById])

  const actorsWithCasts = useMemo(() => {
    return timeline.actors.filter((actor) => actor.casts.length > 0)
  }, [timeline.actors])

  const sortedActors = useMemo(() => {
    return actorsWithCasts
      .map((actor, actorIndex) => ({
        actor,
        actorIndex,
        sortMeta: getTeamPlanActorSortMeta(actor, specializations, 'Unknown'),
      }))
      .sort((a, b) => {
        if (a.sortMeta.roleIndex !== b.sortMeta.roleIndex) {
          return a.sortMeta.roleIndex - b.sortMeta.roleIndex
        }
        return a.sortMeta.sortKey.localeCompare(b.sortMeta.sortKey)
      })
  }, [actorsWithCasts, specializations])

  const actorSummariesBySimpleRole = useMemo(() => {
    const roleBuckets = simpleCharacterRoles.reduce<
      Record<SimpleRole, ActorSummary[]>
    >((acc, role) => {
      acc[role] = []
      return acc
    }, {} as Record<SimpleRole, ActorSummary[]>)

    sortedActors.forEach(({ actor, actorIndex, sortMeta }) => {
      const specialization = actor.spec_id
        ? specializationsById[actor.spec_id]
        : undefined
      if (!specialization) return
      const simpleRole = simpleCharacterRoles[sortMeta.roleIndex]
      if (!simpleRole) return
      roleBuckets[simpleRole].push({
        key: `${actor.label}-${actorIndex}`,
        className:
          specialization.className ||
          getClass(actor.class_id.toString()) ||
          'Unknown class',
        specName: specialization.name,
        specIcon: specialization.icon,
      })
    })

    return roleBuckets
  }, [sortedActors, specializationsById])

  const classOnlyActorSummaries = useMemo(() => {
    return sortedActors.reduce<ActorSummary[]>((acc, { actor, actorIndex }) => {
      const specialization = actor.spec_id
        ? specializationsById[actor.spec_id]
        : undefined
      if (specialization) return acc
      acc.push({
        key: `${actor.label}-${actorIndex}`,
        className: getClass(actor.class_id.toString()) || 'Unknown class',
      })
      return acc
    }, [])
  }, [sortedActors, specializationsById])

  const totalCasts = useMemo(() => {
    const actorCasts = timeline.actors.reduce(
      (count, actor) => count + actor.casts.length,
      0,
    )
    const generalCasts = timeline.general?.casts.length ?? 0
    return actorCasts + generalCasts
  }, [timeline.actors, timeline.general?.casts.length])
  const totalAssignments = useMemo(() => {
    return getEncounterAssignmentAbilities(timeline).filter((ability) =>
      ability.casts.some((cast) =>
        cast.boxes.some((box) => box.entities.length > 0),
      ),
    ).length
  }, [timeline])

  const visibleRoles = useMemo(() => {
    return simpleCharacterRoles.filter(
      (role) => (actorSummariesBySimpleRole[role] ?? []).length > 0,
    )
  }, [actorSummariesBySimpleRole])
  const hasActors = actorsWithCasts.length > 0

  return (
    <Card className={cn('px-2.5 py-1.5', className)}>
      <div className='flex items-center gap-2 text-xs flex-wrap'>
        {visibleRoles.map((role, roleIndex) => (
          <Fragment key={role}>
            <div className='flex items-center gap-1.5'>
              <CharacterRoleIcon role={role} size='small' />
              <span className='text-muted-foreground font-semibold text-xs'>
                {(actorSummariesBySimpleRole[role] ?? []).length}
              </span>
              <div className='flex items-center gap-1'>
                {(actorSummariesBySimpleRole[role] ?? []).map(
                  (actorSummary) => (
                    <Tooltip key={actorSummary.key} disableHoverableContent>
                      <TooltipTrigger asChild>
                        {actorSummary.specIcon ? (
                          <span className='inline-flex'>
                            <BlizzardIcon
                              iconName={actorSummary.specIcon}
                              size='small'
                              className='rounded-sm'
                            />
                          </span>
                        ) : (
                          <span className='inline-flex'>
                            <CharacterClassIcon
                              characterClass={
                                actorSummary.className as CharacterClass
                              }
                              size='small'
                            />
                          </span>
                        )}
                      </TooltipTrigger>
                      <TooltipContent>
                        {actorSummary.specName
                          ? `${actorSummary.specName} ${actorSummary.className}`
                          : actorSummary.className}
                      </TooltipContent>
                    </Tooltip>
                  ),
                )}
              </div>
            </div>
            {roleIndex < visibleRoles.length - 1 && (
              <div className='h-4 w-px bg-border' />
            )}
          </Fragment>
        ))}
        {classOnlyActorSummaries.length > 0 && (
          <>
            <div className='h-4 w-px bg-border' />
            <div className='flex items-center gap-1'>
              {classOnlyActorSummaries.map((actorSummary) => (
                <Tooltip key={actorSummary.key} disableHoverableContent>
                  <TooltipTrigger asChild>
                    <span className='inline-flex'>
                      <CharacterClassIcon
                        characterClass={
                          actorSummary.className as CharacterClass
                        }
                        size='small'
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{actorSummary.className}</TooltipContent>
                </Tooltip>
              ))}
            </div>
          </>
        )}
        {hasActors && <div className='h-4 w-px bg-border' />}
        <div className='flex items-center gap-1'>
          <span className='text-muted-foreground'>Casts:</span>
          <span className='font-semibold min-w-4'>{totalCasts}</span>
        </div>
        {totalAssignments > 0 && <div className='h-4 w-px bg-border' />}
        {totalAssignments > 0 && (
          <div className='flex items-center gap-1'>
            <span className='text-muted-foreground'>Assignments:</span>
            <span className='font-semibold min-w-4'>{totalAssignments}</span>
          </div>
        )}
      </div>
    </Card>
  )
}

export default TeamPlanCompositionSummary
